/**
 * Cart Add-On Items Display Helper
 * Helps display add-on relationships in the cart and handle quantity synchronization.
 * Also enforces cart rules:
 *   Rule 1: Removes orphaned add-ons (add-ons without their main product)
 *   Rule 2: Removes the group when its add-ons don't match the main item's
 *   _requires contract (stamped at add time). No global add-on list is used,
 *   so standalone products are never touched.
 * Also blocks navigation to /checkout until the cart has been cleaned up.
 */

(function () {
    'use strict';
    // Bump SYNC_VERSION on every change and re-copy this file to
    // futurer/assets/cart-addon-item-display.js (see README-IMPORTANT.md there).
    // The PDP block also loads this file; a version mismatch between the two
    // copies means the theme is running stale cart logic.
    const SYNC_VERSION = '2026-10-09-contract-v1';

    if (window.__CART_ADDON_SYNC_VERSION && window.__CART_ADDON_SYNC_VERSION !== SYNC_VERSION) {
        console.warn(
            '[CartAddonDisplay] Version mismatch: loaded ' + SYNC_VERSION +
            ' but page already has ' + window.__CART_ADDON_SYNC_VERSION +
            '. Re-copy the extension file into futurer/assets/.'
        );
    }
    window.__CART_ADDON_SYNC_VERSION = SYNC_VERSION;

    // A second copy with the SAME version would wrap fetch/XHR again and run
    // every removal twice.

    // Loaded by both the PDP block and the theme copy. A second copy would
    // wrap fetch/XHR again and run every removal twice.
    if (window.CartAddonDisplay) return;

    const CartAddonDisplay = {
        cachedCart: null,
        addonConfig: null,
        isEnforcing: false,

        /**
         * Initialize cart display enhancements
         */
        init: function () {
            this.loadAddonConfig();
            this.refreshCartCache(); // Initial cache load
            this.interceptCartAPI(); // Intercept cart API calls
            this.enhanceCartDisplay();
            this.setupQuantitySync();
            this.setupBundleRemoval();
            this.setupCheckoutGuard();
            this.listenForCartUpdates();

            // Enforce cart rules on page load
            this.enforceCartRules();

            // Periodically refresh cart cache (every 30s as a fallback)
            setInterval(() => this.refreshCartCache(), 30000);
        },

        /**
         * Refresh the cart cache for fast lookup
         */
        refreshCartCache: async function () {
            try {
                const nativeFetch = this._nativeFetch || window.fetch;
                const res = await nativeFetch('/cart.js');
                this.cachedCart = await res.json();
                return this.cachedCart;
            } catch (e) {
                console.warn('[CartAddonDisplay] Failed to refresh cart cache');
            }
        },

        /**
         * Intercept cart API calls (fetch + XHR) so that removing — or
         * decreasing the quantity of — any item that belongs to a bundle
         * removes the entire bundle (every line sharing the same _group_id).
         *
         * This path is DOM-independent: it reads the current cart from /cart.js
         * and maps the request's `line` / `id` / `updates` back to the line
         * item, so it works regardless of how the theme renders the cart.
         */
        interceptCartAPI: function () {
            const self = this;

            // Keep the native fetch so internal cart mutations never re-enter
            // this interceptor (which would recurse or double-remove).
            const originalFetch = window.fetch;
            self._nativeFetch = originalFetch;

            // 1. Intercept the Fetch API
            window.fetch = async function (...args) {
                const [url, options] = args;
                const urlString = typeof url === 'string' ? url : (url instanceof URL ? url.toString() : '');

                if (urlString.includes('/cart/change') || urlString.includes('/cart/update')) {
                    const groupId = await self.findGroupedRemoval(urlString, options);
                    if (groupId) {
                        await self.removeGroupByGroupId(groupId);
                        return new Response(JSON.stringify({}), {
                            status: 200,
                            headers: { 'Content-Type': 'application/json' }
                        });
                    }
                }
                return originalFetch(...args);
            };

            // 2. Intercept XMLHttpRequest (XHR)
            const originalOpen = XMLHttpRequest.prototype.open;
            const originalSend = XMLHttpRequest.prototype.send;

            XMLHttpRequest.prototype.open = function (method, url) {
                this._url = url;
                this._method = method;
                return originalOpen.apply(this, arguments);
            };

            XMLHttpRequest.prototype.send = function (body) {
                const url = this._url;
                if (typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update'))) {
                    const groupId = self.findGroupedRemovalSync(url, body);
                    if (groupId) {
                        self.removeGroupByGroupId(groupId);
                        return; // suppress the original single-line change
                    }
                }
                return originalSend.apply(this, arguments);
            };
        },

        /**
         * Synchronous check against the cached cart. Returns the _group_id if
         * the request removes/decreases a grouped item, otherwise null.
         */
        findGroupedRemovalSync: function (url, body) {
            if (!this.cachedCart) return null;
            try {
                const bodyData = this.parseCartBody(body, url);
                return this.detectGroupedRemoval(bodyData, this.cachedCart.items);
            } catch (e) {
                return null;
            }
        },

        /**
         * Async check against a freshly-fetched cart. Returns the _group_id if
         * the request removes/decreases a grouped item, otherwise null.
         */
        findGroupedRemoval: async function (url, options) {
            try {
                const cart = await this.refreshCartCache();
                if (!cart) return null;
                const bodyData = this.parseCartBody(options && options.body, url);
                return this.detectGroupedRemoval(bodyData, cart.items);
            } catch (e) {
                return null;
            }
        },

        /**
         * Parse a cart change/update request body (JSON string, URLSearchParams,
         * or FormData) plus URL query params into a flat object.
         */
        parseCartBody: function (body, url) {
            const bodyData = {};
            if (body) {
                if (typeof body === 'string') {
                    try {
                        Object.assign(bodyData, JSON.parse(body));
                    } catch (e) {
                        const params = new URLSearchParams(body);
                        params.forEach((v, k) => { bodyData[k] = v; });
                    }
                } else if (body instanceof FormData) {
                    body.forEach((v, k) => { bodyData[k] = v; });
                }
            }
            try {
                const urlObj = new URL(url, window.location.origin);
                urlObj.searchParams.forEach((v, k) => { bodyData[k] = v; });
            } catch (e) { /* ignore malformed URL */ }
            return bodyData;
        },

        /**
         * Given parsed request data and the current cart items, return the
         * _group_id of the item being removed/decreased, or null if the change
         * doesn't touch a grouped item.
         */
        detectGroupedRemoval: function (bodyData, items) {
            if (!Array.isArray(items)) return null;

            const look = (id, line, qty) => {
                let item = null;
                if (line !== undefined && line !== null && line !== '') {
                    item = items[parseInt(line) - 1];
                } else if (id !== undefined && id !== null && id !== '') {
                    item = items.find(i => String(i.id) === String(id) || i.key === String(id));
                }
                if (!item) return null;
                const groupId = item.properties && item.properties._group_id;
                if (!groupId) return null;
                const newQty = parseInt(qty);
                if (newQty === 0 || newQty < item.quantity) return groupId;
                return null;
            };

            // Single-line change: {id|line, quantity}
            if ((bodyData.id || bodyData.line) && bodyData.quantity !== undefined) {
                return look(bodyData.id, bodyData.line, bodyData.quantity);
            }

            // Bulk update: {updates: {key|lineIndex: qty}}
            if (bodyData.updates && typeof bodyData.updates === 'object') {
                for (const [key, qty] of Object.entries(bodyData.updates)) {
                    const viaKey = look(key, undefined, qty);
                    if (viaKey) return viaKey;
                    if (!isNaN(key) && key.length < 3) {
                        const viaLine = look(undefined, key, qty);
                        if (viaLine) return viaLine;
                    }
                }
            }

            // Form-style updates[N]=qty
            for (const key in bodyData) {
                if (key.startsWith('updates[')) {
                    const m = key.match(/updates\[(\d+)\]/);
                    if (m) {
                        const viaLine = look(undefined, m[1], bodyData[key]);
                        if (viaLine) return viaLine;
                    }
                }
            }

            return null;
        },

        /**
         * Enhance cart display to show add-on relationships grouped by bundle
         */
        enhanceCartDisplay: function () {
            const cartItems = document.querySelectorAll('.cart-item, [data-cart-item]');
            const bundles = {};

            // First pass: organize items into bundles
            cartItems.forEach(item => {
                const properties = this.getItemProperties(item);
                const groupId = properties['_group_id'];

                if (groupId) {
                    if (!bundles[groupId]) {
                        bundles[groupId] = {
                            parent: null,
                            addons: [],
                            total: 0
                        };
                    }

                    const isMainProduct = properties['_is_main_product'];
                    if (isMainProduct) {
                        bundles[groupId].parent = { item, properties };
                    } else {
                        bundles[groupId].addons.push({ item, properties });
                    }
                }
            });

            // Second pass: apply visual grouping
            Object.entries(bundles).forEach(([groupId, bundle]) => {
                if (bundle.parent && bundle.addons.length > 0) {
                    this.groupBundleItems(groupId, bundle);
                }
            });
        },

        /**
         * Group bundle items visually on the cart page
         */
        groupBundleItems: function (groupId, bundle) {
            const parentItem = bundle.parent.item;

            // Mark parent
            parentItem.classList.add('cart-bundle-parent');
            parentItem.dataset.groupId = groupId;

            // Add bundle indicator to parent
            this.addBundleIndicator(parentItem, bundle);

            // Style and position add-ons
            bundle.addons.forEach((addon, index) => {
                const addonItem = addon.item;
                addonItem.classList.add('cart-bundle-addon');
                addonItem.dataset.groupId = groupId;

                // Move addon right after parent (or after previous addon)
                const insertAfter = index === 0 ? parentItem : bundle.addons[index - 1].item;
                if (insertAfter.nextSibling !== addonItem) {
                    insertAfter.parentNode.insertBefore(addonItem, insertAfter.nextSibling);
                }

                // Style the addon
                this.styleAddonItem(addonItem, addon.properties);
            });

            // Add bundle total
            this.addBundleTotal(bundle.addons[bundle.addons.length - 1].item, groupId);
        },

        /**
         * Add bundle indicator to parent item
         */
        addBundleIndicator: function (parentItem, bundle) {
            const addonNames = bundle.addons
                .map(a => a.properties['_addon_type'] || 'Add-on')
                .join(', ');

            const badge = document.createElement('div');
            badge.className = 'cart-addon-badge';
            badge.innerHTML = `
                <style>
                    .cart-addon-badge {
                        display: inline-block;
                        background: #eff6ff;
                        color: #3b82f6;
                        padding: 0.25rem 0.5rem;
                        border-radius: 4px;
                        font-size: 0.75rem;
                        margin-top: 0.5rem;
                        font-weight: 500;
                    }
                </style>
                <span>📦 Bundle includes: ${addonNames}</span>
            `;

            const titleEl = parentItem.querySelector('.cart-item__name, .cart-item-title, [data-cart-item-title]');
            if (titleEl && !parentItem.querySelector('.cart-addon-badge')) {
                titleEl.parentNode.insertBefore(badge, titleEl.nextSibling);
            }
        },

        /**
         * Style addon item with indentation and badge
         */
        styleAddonItem: function (addonItem, properties) {
            const addonType = properties['_addon_type'] || 'Add-on';

            const badge = document.createElement('div');
            badge.className = 'cart-addon-item-badge';
            badge.innerHTML = `
                <style>
                    .cart-addon-item-badge {
                        display: inline-block;
                        background: #f0fdf4;
                        color: #059669;
                        padding: 0.25rem 0.5rem;
                        border-radius: 4px;
                        font-size: 0.75rem;
                        margin-top: 0.5rem;
                        font-weight: 500;
                    }
                    .cart-bundle-addon {
                        padding-left: 2rem !important;
                        border-left: 3px solid #059669;
                        background: #f9fafb;
                    }
                </style>
                <span>➕ Add-on: ${addonType}</span>
            `;

            const titleEl = addonItem.querySelector('.cart-item__name, .cart-item-title, [data-cart-item-title]');
            if (titleEl && !addonItem.querySelector('.cart-addon-item-badge')) {
                titleEl.parentNode.insertBefore(badge, titleEl.nextSibling);
            }
        },

        /**
         * Add bundle total row after the last addon item
         */
        addBundleTotal: function (lastAddonItem, groupId) {
            if (lastAddonItem.nextSibling?.classList?.contains('cart-bundle-total')) {
                return; // Already added
            }

            const totalRow = document.createElement('div');
            totalRow.className = 'cart-bundle-total';
            totalRow.dataset.groupId = groupId;
            totalRow.innerHTML = `
                <style>
                    .cart-bundle-total {
                        padding: 0.75rem 1rem;
                        background: #fef3c7;
                        border-left: 3px solid #f59e0b;
                        margin: 0.5rem 0;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        font-weight: 600;
                        color: #92400e;
                    }
                </style>
                <div class="cart-bundle-total-label">🎁 Bundle Total</div>
                <div class="cart-bundle-total-amount" data-bundle-total="${groupId}">Calculating...</div>
            `;

            lastAddonItem.parentNode.insertBefore(totalRow, lastAddonItem.nextSibling);
            this.updateBundleTotal(groupId);
        },

        /**
         * Update bundle total amount
         */
        updateBundleTotal: async function (groupId) {
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();

                let total = 0;
                cart.items.forEach(item => {
                    if (item.properties?._group_id === groupId) {
                        total += item.final_line_price;
                    }
                });

                const formattedTotal = this.formatMoney(total, cart.currency);
                const totalEl = document.querySelector(`[data-bundle-total="${groupId}"]`);
                if (totalEl) {
                    totalEl.textContent = formattedTotal;
                }
            } catch (error) {
                console.error('[CartAddonDisplay] Error updating bundle total:', error);
            }
        },

        /**
         * Format money from cents
         */
        formatMoney: function (cents, currency = 'USD') {
            const amount = cents / 100;
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency
            }).format(amount);
        },

        /**
         * Get item properties from cart item element
         */
        getItemProperties: function (item) {
            const properties = {};
            const propElements = item.querySelectorAll('[data-cart-item-property-name]');

            propElements.forEach(el => {
                const name = el.dataset.cartItemPropertyName;
                const value = el.dataset.cartItemPropertyValue || el.textContent.trim();
                properties[name] = value;
            });

            return properties;
        },

        /**
         * Setup quantity synchronization between main products and add-ons
         */
        setupQuantitySync: function () {
            const quantityInputs = document.querySelectorAll('.cart-item__quantity-input, [data-cart-item-quantity], input[name="updates[]"]');

            quantityInputs.forEach(input => {
                input.addEventListener('change', (e) => {
                    this.handleQuantityChange(e.target);
                });
            });
        },

        /**
         * Handle quantity change for bundle items
         */
        handleQuantityChange: async function (input) {
            const cartItem = input.closest('.cart-item, [data-cart-item]');
            if (!cartItem) return;

            const groupId = cartItem.dataset.groupId;
            if (!groupId) return; // No group ID, let normal cart behavior handle it

            const newQuantity = parseInt(input.value);

            // If quantity is reduced or set to 0, remove the entire group
            if (newQuantity === 0) {
                await this.removeEntireGroup(groupId);
                return;
            }

            // Any quantity reduction removes the whole group
            const currentQuantity = parseInt(input.getAttribute('data-previous-quantity') || input.defaultValue || 1);
            if (newQuantity < currentQuantity) {
                if (confirm('Reducing quantity will remove this bundle. Continue?')) {
                    await this.removeGroupByGroupId(groupId);
                } else {
                    input.value = currentQuantity;
                }
                return;
            }

            // If quantity increased, sync all items in the group
            await this.syncBundleQuantities(groupId, newQuantity);
        },

        /**
         * Remove every cart line sharing the given bundle _group_id.
         */
        removeGroupByGroupId: async function (groupId) {
            try {
                const cart = await this.refreshCartCache();
                if (!cart || !cart.items) return;

                const updates = {};
                let removed = 0;
                cart.items.forEach(item => {
                    if (item.properties?._group_id === groupId) {
                        updates[item.key] = 0;
                        removed++;
                    }
                });

                if (removed === 0) return;

                const nativeFetch = this._nativeFetch || window.fetch;
                await nativeFetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ updates })
                });

                window.location.reload();
            } catch (error) {
                console.error('[CartAddonDisplay] Error removing group:', error);
            }
        },

        /**
         * Remove an entire group by its bundle _group_id
         */
        removeEntireGroup: async function (groupId) {
            await this.removeGroupByGroupId(groupId);
        },

        /**
         * Sync all items in a bundle to the same quantity
         */
        syncBundleQuantities: async function (groupId, newQuantity) {
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();

                const updates = {};
                cart.items.forEach(item => {
                    if (item.properties?._group_id === groupId) {
                        updates[item.key] = newQuantity;
                    }
                });

                await this.updateCart(updates);

                // Reload to show updated quantities
                window.location.reload();
            } catch (error) {
                console.error('[CartAddonDisplay] Error syncing quantities:', error);
            }
        },

        /**
         * Setup bundle removal handling
         */
        setupBundleRemoval: function () {
            // Intercept click-based removals (remove links/buttons)
            document.addEventListener('click', (e) => {
                const removeLink = e.target.closest('a[href*="/cart/change"], button[data-cart-remove]');
                if (!removeLink) return;

                const cartItem = removeLink.closest('.cart-item, [data-cart-item]');
                if (!cartItem) return;

                const groupId = cartItem.dataset.groupId;
                if (!groupId) return; // No group ID, let normal cart behavior handle it

                // Removing ANY item with a group_id removes the entire group
                e.preventDefault();
                e.stopPropagation();
                this.removeBundleConfirm(groupId);
            });

            // Intercept form-based cart updates
            const cartForms = document.querySelectorAll('form[action="/cart"], form[action*="/cart/update"]');
            cartForms.forEach(form => {
                form.addEventListener('submit', async (e) => {
                    const hasGroupedItems = await this.checkForGroupedItemRemoval(form);
                    if (hasGroupedItems) {
                        e.preventDefault();
                    }
                });
            });
        },

        /**
         * Check if form submission would remove any grouped items
         */
        checkForGroupedItemRemoval: async function (form) {
            try {
                const formData = new FormData(form);
                const response = await fetch('/cart.js');
                const cart = await response.json();

                let groupToRemove = null;

                for (const [key, value] of formData.entries()) {
                    const quantity = parseInt(value);
                    let item = null;

                    if (key.startsWith('updates[')) {
                        const lineMatch = key.match(/updates\[(\d+)\]/);
                        if (lineMatch) {
                            item = cart.items[parseInt(lineMatch[1]) - 1];
                        } else {
                            const keyName = key.replace('updates[', '').replace(']', '');
                            item = cart.items.find(i => i.id.toString() === keyName || i.key === keyName);
                        }
                    }

                    if (item?.properties?._group_id) {
                        if (quantity === 0 || quantity < item.quantity) {
                            groupToRemove = item.properties._group_id;
                            break;
                        }
                    }
                }

                if (groupToRemove) {
                    await this.removeGroupByGroupId(groupToRemove);
                    return true;
                }

                return false;
            } catch (error) {
                return false;
            }
        },

        /**
         * Confirm and remove the whole bundle when removing a grouped item
         */
        removeBundleConfirm: async function (groupId) {
            if (!confirm('Removing this item will remove the whole bundle. Continue?')) {
                return;
            }

            await this.removeGroupByGroupId(groupId);
        },

        /**
         * Update cart via AJAX
         */
        updateCart: async function (updates) {
            try {
                const nativeFetch = this._nativeFetch || window.fetch;
                const response = await nativeFetch('/cart/update.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ updates })
                });

                if (!response.ok) {
                    throw new Error('Failed to update cart');
                }

                const result = await response.json();

                // Trigger cart refresh
                document.dispatchEvent(new CustomEvent('cart:updated'));

                return result;
            } catch (error) {
                console.error('[CartAddonDisplay] Error updating cart:', error);
            }
        },

        /**
         * Load add-on configuration embedded by the Liquid block
         */
        loadAddonConfig: function () {
            try {
                const el = document.getElementById('addon-config-data');
                if (el) {
                    let parsed = JSON.parse(el.textContent);
                    // The embedded JSON may be a nested array (array of arrays)
                    // because each metaobject entry's configuration field is itself an array.
                    // Flatten one level so we get a flat array of product configs.
                    this.addonConfig = parsed.flat();
                } else {
                    this.addonConfig = [];
                }
            } catch (e) {
                console.warn('[CartAddonDisplay] Failed to parse addon config:', e);
                this.addonConfig = [];
            }
        },

        /**
         * Enforce cart rules:
         *   Rule 1: Remove orphaned add-ons (add-ons whose main product is missing)
         *   Rule 2: Remove the group when its add-ons don't match the main
         *   item's _requires contract. Resolves true when lines were removed.
         */
        enforceCartRules: function () {
            // Shared in-flight run: page load, cart:updated and the checkout
            // guard all await the same result. Resolves true when lines were
            // removed (page reloading), false otherwise. Fail open on errors.
            if (this._enforcePromise) return this._enforcePromise;
            this._enforcePromise = this._runEnforcement().finally(() => {
                this._enforcePromise = null;
            });
            return this._enforcePromise;
        },

        _runEnforcement: async function () {
            try {
                const cart = await this.refreshCartCache();
                if (!cart || !cart.items || cart.items.length === 0) {
                    return false;
                }

                // Group items by _group_id
                const groups = {};
                for (const item of cart.items) {
                    const groupId = item.properties?._group_id;
                    if (!groupId) continue;

                    if (!groups[groupId]) {
                        groups[groupId] = { mainItems: [], addonItems: [], allItems: [] };
                    }
                    groups[groupId].allItems.push(item);

                    if (item.properties?._is_main_product) {
                        groups[groupId].mainItems.push(item);
                    } else {
                        groups[groupId].addonItems.push(item);
                    }
                }

                // Collect keys of items to remove (set qty to 0)
                const keysToRemove = new Set();

                for (const [groupId, group] of Object.entries(groups)) {
                    // Rule 1: Orphaned add-ons — add-ons exist but no main product
                    if (group.addonItems.length > 0 && group.mainItems.length === 0) {
                        console.log('[CartAddonDisplay] Rule 1: Removing orphaned add-ons in group', groupId);
                        group.addonItems.forEach(item => keysToRemove.add(item.key));
                        continue;
                    }

                    // Rule 2: group must match the main item's _requires contract.
                    // _requires holds the numeric variant IDs added with the bundle.
                    // Missing, extra, or swapped add-ons invalidate the whole group.
                    // Groups without _requires are legacy bundles: leave them alone.
                    if (group.mainItems.length > 0) {
                        for (const mainItem of group.mainItems) {
                            if (!this.groupMatchesContract(mainItem, group.addonItems)) {
                                console.log('[CartAddonDisplay] Rule 2: Removing group', groupId,
                                    '— add-ons do not match bundle contract');
                                group.allItems.forEach(item => keysToRemove.add(item.key));
                                break;
                            }
                        }
                    }
                }

                // Apply removals
                if (keysToRemove.size > 0) {
                    await this.removeCartItems([...keysToRemove]);
                    // Reload page to reflect the updated cart
                    window.location.reload();
                    return true;
                }
                return false;
            } catch (e) {
                console.error('[CartAddonDisplay] Error enforcing cart rules:', e);
                return false;
            }
        },

        /* checkRequiredAddonsMissing removed: contract travels with the bundle now. */
        /**
         * Check a bundle group against its main item's _requires contract.
         * Valid when the group's add-on variant IDs exactly equal the required set.
         * No _requires property (legacy bundle) or no main item: valid, fail open.
         */
        groupMatchesContract: function (mainItem, addonItems) {
            const raw = mainItem.properties && mainItem.properties._requires;
            if (raw === undefined || raw === null || String(raw).trim() === '') return true;
            const required = new Set(
                String(raw).split(',').map(s => s.trim()).filter(Boolean)
            );
            const present = new Set(
                (addonItems || []).map(i => String(i.variant_id))
            );
            if (required.size !== present.size) return false;
            for (const id of required) {
                if (!present.has(id)) return false;
            }
            return true;
        },

        /**
         * Block every route to /checkout (form posts and links) until enforceCartRules has run.
         * If lines were removed the page reloads and the customer never reaches checkout with
         * an invalid cart; otherwise the original navigation is replayed.
         * Cannot cover Shop Pay / dynamic checkout buttons (rendered in iframes).
         */
        setupCheckoutGuard: function () {
            if (this._checkoutGuardSetup) return;
            this._checkoutGuardSetup = true;
            const isCheckoutHref = (href) => {
                try {
                    return new URL(href, window.location.origin).pathname.replace(/\/$/, '').endsWith('/checkout');
                } catch (e) {
                    return false;
                }
            };
            let bypass = false;

            // <form action="/checkout"> (drawer, popup) and the cart form's name="checkout" submit button
            document.addEventListener('submit', async (e) => {
                if (bypass) return;
                const form = e.target;
                const submitter = e.submitter;
                const toCheckout = isCheckoutHref(form.getAttribute('action') || '')
                    || (submitter && submitter.name === 'checkout');
                if (!toCheckout) return;

                e.preventDefault();
                e.stopImmediatePropagation();
                const removed = await this.enforceCartRules();
                if (removed) return; // page is reloading with the cleaned cart

                bypass = true;
                try {
                    form.requestSubmit(submitter || undefined);
                } finally {
                    bypass = false;
                }
            }, true);

            // <a href="/checkout">
            document.addEventListener('click', async (e) => {
                if (bypass || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                const link = e.target.closest && e.target.closest('a[href]');
                if (!link || !isCheckoutHref(link.getAttribute('href'))) return;

                e.preventDefault();
                e.stopImmediatePropagation();
                const removed = await this.enforceCartRules();
                if (!removed) window.location.assign(link.href);
            }, true);
        },

        /**
         * Remove items from cart by setting their quantities to 0
         */
        removeCartItems: async function (keys) {
            const updates = {};
            keys.forEach(key => { updates[key] = 0; });

            try {
                const nativeFetch = this._nativeFetch || window.fetch;
                await nativeFetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ updates })
                });
            } catch (e) {
                console.error('[CartAddonDisplay] Error removing items:', e);
            }
        },

        /**
         * Listen for cart update events to refresh display
         */
        listenForCartUpdates: function () {
            document.addEventListener('cart:updated', () => {
                setTimeout(() => {
                    this.enhanceCartDisplay();
                    this.enforceCartRules();
                }, 500);
            });
        }
    };

    // Auto-initialize globally
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            CartAddonDisplay.init();
        });
    } else {
        CartAddonDisplay.init();
    }

    // Expose to global scope
    window.CartAddonDisplay = CartAddonDisplay;

})();
